
import { supabase } from "@/integrations/supabase/client";
import { sendInvoiceEmail, sendSubscriptionConfirmationEmail, sendAdminSubscriptionNotification } from "./invoice-utils";

/**
 * Creates a new subscription
 */
export const createNewSubscription = async (
  productId: string | undefined, 
  planId: string, 
  schoolId: string, 
  userEmail: string
) => {
  console.log('🔄 Creating subscription via Edge Function');
  
  const subscriptionData = {
    productId: productId || '',
    planId: planId,
    schoolId: schoolId,
    userEmail: userEmail,
  };
  
  try {
    // Check if this is a Free Trial plan
    const { data: planData, error: planError } = await supabase
      .from('plans')
      .select('name')
      .eq('id', planId)
      .single();
      
    if (planError) {
      console.error('❌ Error checking plan type:', planError);
      throw new Error(`Failed to check plan type: ${planError.message}`);
    }
    
    const isFreeTrialPlan = planData?.name === 'Free Trial';
    
    // For Free Trial plans, check if school already used their trial
    if (isFreeTrialPlan) {
      console.log('ℹ️ This is a Free Trial plan, checking if school already used their trial');
      
      const { data: trial, error: trialError } = await supabase
        .from('school_trials')
        .select('id')
        .eq('school_id', schoolId)
        .maybeSingle();
        
      if (trialError) {
        console.error('❌ Error checking trial status:', trialError);
      } else if (trial) {
        console.error('❌ School has already used their free trial');
        throw new Error('This school has already used their free trial');
      }
    }
    
    console.log('📤 Calling create-subscription Edge Function with data:', subscriptionData);
    const { data: subscriptionResponse, error: subscriptionError } = await supabase.functions.invoke('create-subscription', {
      body: subscriptionData
    });
    
    if (subscriptionError) {
      console.error('❌ Subscription creation error:', subscriptionError);
      
      // More detailed error logging to help diagnose the issue
      if (subscriptionError.message) {
        console.error('❌ Error message:', subscriptionError.message);
        
        // Check if this is a school registration error
        if (subscriptionError.message.includes('SCHOOL_NOT_REGISTERED:') || 
            subscriptionError.message.includes('non-2xx status code')) {
          throw new Error(`SCHOOL_NOT_REGISTERED:${schoolId}`);
        }
      }
      
      throw new Error(`Failed to create subscription: ${subscriptionError.message || 'Unknown error'}`);
    }
    
    if (!subscriptionResponse || !subscriptionResponse.success) {
      console.error('❌ Unsuccessful subscription response:', subscriptionResponse);
      const errorMessage = subscriptionResponse?.error || 'Unknown error creating subscription';
      
      // Check if this is a school registration error
      if (errorMessage.includes('SCHOOL_NOT_REGISTERED:') || errorMessage === 'SCHOOL_NOT_REGISTERED') {
        throw new Error(`SCHOOL_NOT_REGISTERED:${schoolId}`);
      }
      
      throw new Error(errorMessage);
    }
    
    console.log('✅ Subscription created successfully via Edge Function:', subscriptionResponse);
    
    // If there was an error with bill component creation, log it but don't fail the entire operation
    if (subscriptionResponse.billComponentError) {
      console.warn('⚠️ Bill component creation had an issue:', subscriptionResponse.billComponentError);
    }
    
    // If a bill was created in the edge function, we need to log it
    if (subscriptionResponse.data && subscriptionResponse.data.bill) {
      console.log('ℹ️ Bill was created during subscription creation:', subscriptionResponse.data.bill.id);
      
      // Send invoice email if there is a bill in the response
      try {
        console.log('🔄 Sending invoice email for bill:', subscriptionResponse.data.bill.id);
        await sendInvoiceEmail(subscriptionResponse.data.bill.id, userEmail);
        console.log('✅ Invoice email sent successfully');
      } catch (emailError) {
        console.error('❌ Error sending invoice email:', emailError);
        // Don't throw here, just log the error as this shouldn't block the subscription process
      }
    } else {
      console.log('ℹ️ No bill was created during subscription creation. This is normal if using the BillCreator separately.');
    }
    
    // Send subscription confirmation email
    const subscription = subscriptionResponse.data?.subscription;
    if (subscription) {
      try {
        // Fetch product and plan details to include in the email
        const { data: productData } = await supabase
          .from('products')
          .select('name')
          .eq('id', subscription.product_id)
          .single();
          
        const { data: planData } = await supabase
          .from('plans')
          .select('name, price')
          .eq('id', subscription.plan_id)
          .single();
          
        const productName = productData?.name || 'Product';
        const planName = planData?.name || 'Plan';
        const price = planData?.price || 0;
        
        await sendSubscriptionConfirmationEmail(
          userEmail,
          productName,
          planName,
          subscription.end_date,
          price
        );
        
        // Send admin notification email
        await sendAdminSubscriptionNotification(
          schoolId,
          productName,
          planName,
          price,
          userEmail
        );
      } catch (confirmationError) {
        console.error('❌ Error sending confirmation email:', confirmationError);
        // Don't throw here, just log the error as this shouldn't block the subscription process
      }
    }
    
    return subscriptionResponse.data?.subscription;
  } catch (subscriptionError: any) {
    // Improve error handling with more detailed information
    console.error('❌ Error in subscription creation try/catch:', subscriptionError);
    
    if (subscriptionError.message && 
        (subscriptionError.message.includes('SCHOOL_NOT_REGISTERED:') || 
         subscriptionError.message === 'SCHOOL_NOT_REGISTERED' ||
         subscriptionError.message.includes('non-2xx status code'))) {
      throw new Error(`SCHOOL_NOT_REGISTERED:${schoolId}`);
    }
    
    throw subscriptionError;
  }
};
